/**
 * The whole balance as a shape.
 *
 * Its own module because two places need it: the financial strip, where it
 * sits under the figures it divides, and the capital region. A copy in each
 * would be two bars that could disagree about what "free" means.
 */
import type { CapitalResponse, OverviewResponse } from "@/shared/api";
import { isZero, money, percentOf } from "@/shared/money/money";

/**
 * The whole balance, divided into the four parts it is actually in.
 *
 * One bar rather than four figures: the question a reader has is how committed
 * the account is, and that is a shape before it is a number. The segments run
 * free, positions, recovery, MAJOR — least committed to least reachable — and
 * they carry the capital scale, never the result colours. A safety share drawn
 * in the colour that means loss would say the account is down by the amount it
 * is deliberately holding back.
 *
 * The widths are geometry, not money: each share is taken against equity for
 * the bar's own layout, and every figure beside it arrived measured.
 */
export function AllocationBar({
  capital,
  overview,
  caption = true,
}: {
  readonly capital: CapitalResponse | null;
  readonly overview: OverviewResponse;
  /** Off where the panel around it is already headed "распределение": two
   *  headings one under the other are two claims about what follows, and the
   *  reader spends the gap between them working out that they are one. */
  readonly caption?: boolean;
}): React.JSX.Element {
  // The share held by a pool that takes a slice of the account instead of
  // buying slots out of the same budget. BASE v1.3 had one and called it
  // MAJOR; BASE v2.0 creates none, and the API goes on listing the pool with
  // nothing in it — a rule that still exists, funded with zero.
  //
  // A segment of zero width under a legend entry reading "Резерв MAJOR 0.00"
  // is a name for nothing: it costs a quarter of the legend and tells the
  // reader about a pool the account cannot put money into. It returns the
  // moment the pool holds anything, because then it is a fact about where the
  // money is.
  const funded = capital?.pools.find((pool) => pool.pool === "MAJOR") ?? null;
  const reserving = funded === null || isZero(funded.allocated) ? null : funded;
  const segments = [
    {
      key: "free",
      label: "Свободно",
      amount: overview.strategy_free_capital,
      fill: "bg-capital-free",
    },
    {
      key: "positions",
      label: "Позиции",
      amount: overview.position_margin,
      fill: "bg-capital-positions",
    },
    {
      key: "recovery",
      label: "Усреднение",
      amount: overview.reserved_grid_capital,
      fill: "bg-capital-recovery",
    },
    ...(overview.withheld_profit == null || isZero(overview.withheld_profit)
      ? []
      : [
          {
            key: "withheld",
            label: "К выводу",
            amount: overview.withheld_profit,
            fill: "bg-capital-safety",
          },
        ]),
    ...(reserving === null
      ? []
      : [
          {
            key: "major",
            label: `Резерв ${reserving.pool}`,
            amount: reserving.allocated,
            fill: "bg-capital-safety",
          },
        ]),
  ] as const;

  return (
    <div className="flex flex-col gap-1.5" data-testid="allocation-bar">
      {/* Caption and legend on one line above the bar, as the mock builds it.
          A legend under the bar makes the reader look down to learn what they
          just looked at, and the caption alone would leave a bare line of
          colour with no name. */}
      <div
        className={`flex flex-wrap items-center gap-x-4 gap-y-1 ${
          // With the caption on, the row is caption-left legend-right and
          // balanced. With it off there is nothing on the left, and a legend
          // pushed to the far edge hangs over the middle of the bar with a
          // hand's width of nothing beside it — so it starts where the bar
          // starts instead.
          caption ? "justify-between" : "justify-start"
        }`}
      >
        {caption && (
          <span className="text-label-caps text-text-muted">
            Распределение капитала
          </span>
        )}
        <ul className="flex flex-wrap gap-x-4 gap-y-1">
          {segments.map((segment) => (
            <li key={segment.key} className="flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className={`size-2 shrink-0 rounded-row ${segment.fill}`}
              />
              <span className="text-label-caps text-text-muted">
                {segment.label}
              </span>
              <span className="text-label-caps tabular-nums text-text-secondary">
                {money(segment.amount)}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <span
        aria-hidden="true"
        className="flex h-3 w-full overflow-hidden rounded-row border border-border-subtle bg-surface-hover"
      >
        {segments.map((segment) => (
          <span
            key={segment.key}
            data-testid={`allocation-segment-${segment.key}`}
            title={`${segment.label}: ${money(segment.amount)}`}
            className={`block h-full ${segment.fill}`}
            style={{ width: `${percentOf(segment.amount, overview.equity)}%` }}
          />
        ))}
      </span>
    </div>
  );
}
